import { T, useTheme, dens, Card, Eyebrow, Badge, StatusBadge } from '../../../design';
import type { Claim } from '../../../types/claims.types';
import { fmtTimestampDate, fmtTimestampTime } from '../../../utils';
import { TYPE_LABEL } from '../claimsConfig';
import { Thumb } from './shared';

function Info({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: T.fg3, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: T.fg, fontFamily: mono ? 'ui-monospace, monospace' : T.font }}>{value || '—'}</div>
    </div>
  );
}

export function ClaimDetail({ claim }: { claim: Claim }) {
  const t = useTheme();
  const d = dens(t);
  const processed = claim.status !== 'open';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: d.gap }}>
      <Card pad={16}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <StatusBadge status={claim.status} />
          <span style={{ fontSize: 12, color: T.fg3, fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}>#{claim.id}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 14px', marginTop: 14 }}>
          <Info label="Oprettet" value={`${fmtTimestampDate(claim.createdAt)} · ${fmtTimestampTime(claim.createdAt)}`} />
          <Info label="Ønsket udfald" value={claim.preferredOutcome === 'credited' ? 'Kreditering' : 'Erstatningsvare'} />
          <Info label="Salon" value={claim.salonName} />
          <Info label="Oprettet af" value={claim.createdByName} />
        </div>
      </Card>

      {processed && (
        <Card pad={16} style={{ background: claim.status === 'approved' ? T.accentLight : T.errorBg, border: `1px solid ${claim.status === 'approved' ? T.border : T.errorBorder}` }}>
          <Eyebrow color={claim.status === 'approved' ? t.accent : T.error}>PARK's behandling</Eyebrow>
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {claim.outcome && <Info label="Udfald" value={claim.outcome === 'credited' ? 'Krediteret' : claim.outcome === 'replacement_sent' ? 'Erstatning sendt' : 'Afvist'} />}
            {claim.productHandling && <Info label="Produkthåndtering" value={claim.productHandling === 'discard' ? 'Kassér' : 'GLS-afhentning'} />}
            {claim.creditNoteNumber && <Info label="Kreditnota" value={claim.creditNoteNumber} mono />}
            {claim.processedByName && <Info label="Behandlet af" value={`${claim.processedByName} · ${claim.processedAt ? fmtTimestampDate(claim.processedAt) : ''}`} />}
          </div>
        </Card>
      )}

      <Eyebrow style={{ padding: '4px 2px 0' }}>Varelinjer · {claim.items.length}</Eyebrow>
      {claim.items.map((it) => (
        <Card key={it.id ?? it.sku} pad={14}>
          <div style={{ display: 'flex', gap: 13 }}>
            <Thumb size={56} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.fg }}>{it.productName}</div>
              <div style={{ fontSize: 11.5, color: T.fg3, fontWeight: 600, marginTop: 3 }}><span style={{ fontFamily: 'ui-monospace, monospace' }}>{it.sku}</span> · {it.quantity} stk.</div>
              <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <Badge tone="neutral">{TYPE_LABEL[it.claimType]}</Badge>
                {it.orderNumber && <Badge tone="neutral">Ordre {it.orderNumber}</Badge>}
                {it.isB2c && <Badge tone="neutral" dot>B2C{it.b2cCustomerName ? ` · ${it.b2cCustomerName}` : ''}</Badge>}
              </div>
            </div>
          </div>
          {it.description && <div style={{ marginTop: 12, fontSize: 13, color: T.fg2, lineHeight: 1.5, background: T.bg, borderRadius: 10, padding: '10px 12px' }}>{it.description}</div>}
          {it.media > 0 && <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>{Array.from({ length: it.media }).map((_, i) => <Thumb key={i} size={48} label="foto" />)}</div>}
        </Card>
      ))}
    </div>
  );
}
